import { useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LeftPanel from './LeftPanel';
import { useAuthStore } from '@/store/authStore';

export default function AuthLayout() {
  const { isAuthenticated, clearError } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated — except on /reset-password, so a
  // logged-in user who clicks a reset-link email can still set a new
  // password instead of being bounced to /home.
  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/reset-password') {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  // The auth error/errorCode live in the shared store; without this, a
  // failed login's message follows the user onto /register or
  // /forgot-password and renders there as if that page had failed.
  useEffect(() => {
    clearError();
  }, [location.pathname, clearError]);

  return (
    <div className="min-h-screen w-full flex flex-row bg-slate-50 overflow-hidden">
      {/* Left Panel - Desktop Only (STAYS STATIC) */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[50%] flex-shrink-0">
        <div className="w-full h-screen overflow-hidden">
          <LeftPanel />
        </div>
      </div>

      {/* Right Panel - Form Area (TRANSITIONS) */}
      <div className="flex-1 min-w-0 flex items-center justify-center p-6 lg:p-12 bg-slate-50 relative">
        {/* Background Decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        </div>

        <div className="w-full max-w-md relative z-10">
          {/* Mobile Logo - Only visible on mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden flex flex-col items-center mb-8"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/30 mb-4">
              <span className="text-white font-bold text-2xl">D</span>
            </div>
            <span className="text-slate-900 text-2xl font-bold tracking-tight">Decixa</span>
            <p className="text-slate-500 text-sm mt-1">Make better decisions together</p>
          </motion.div>

          {/* Form Content - Animated transitions between pages */}
          <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-8 lg:p-10 border border-slate-100 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center text-sm text-slate-500"
          >
            <p>
              By continuing, you agree to our{' '}
              <a href="/terms-and-conditions" className="text-purple-600 hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-purple-600 hover:underline">
                Privacy Policy
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}